// Retention for the emergency session backups kept in localStorage / sessionStorage.
// Browser storage is only ~5–10 MB; without pruning, every sedation left its backups behind until
// writes failed with QuotaExceededError and the app started crashing.

export const BACKUPS_PER_SEDATION = 5;
// ponytail: fixed count, not a byte budget; lower it (or measure sizes) if sessions grow much larger
export const SEDATIONS_WITH_BACKUPS = 10;

export interface BackupEntry {
  key: string;
  sessionId: string;
  time: number; // ms since epoch; 0 when unknown (treated as oldest)
}

/**
 * Which backup keys to delete: everything beyond the newest BACKUPS_PER_SEDATION per sedation,
 * and every backup of sedations outside the SEDATIONS_WITH_BACKUPS most recently backed up.
 * The active sedation is always kept.
 */
export const backupKeysToDelete = (
  entries: BackupEntry[],
  activeSessionId?: string,
  perSedation = BACKUPS_PER_SEDATION,
  sedations = SEDATIONS_WITH_BACKUPS
): string[] => {
  const bySession = new Map<string, BackupEntry[]>();
  for (const entry of entries) {
    bySession.set(entry.sessionId, [...(bySession.get(entry.sessionId) ?? []), entry]);
  }

  const sessionsNewestFirst = [...bySession.entries()]
    .map(([sessionId, list]) => ({ sessionId, list: [...list].sort((a, b) => b.time - a.time) }))
    .sort((a, b) => b.list[0].time - a.list[0].time);

  const keptSessions = new Set(sessionsNewestFirst.slice(0, sedations).map(s => s.sessionId));
  if (activeSessionId) keptSessions.add(activeSessionId);

  return sessionsNewestFirst.flatMap(({ sessionId, list }) =>
    keptSessions.has(sessionId) ? list.slice(perSedation).map(e => e.key) : list.map(e => e.key)
  );
};

const BACKUP_KEY = /^(session-backup-|fallback-backup-|emergency_backup_)/;

/** Reads every backup in a Storage area as entries (sessionId and timestamp come from the stored JSON). */
export const readBackupEntries = (storage: Storage): BackupEntry[] => {
  const entries: BackupEntry[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !BACKUP_KEY.test(key)) continue;
    let sessionId = key.startsWith('emergency_backup_') ? key.slice('emergency_backup_'.length) : '';
    let time = 0;
    try {
      const value = JSON.parse(storage.getItem(key) ?? 'null');
      sessionId = value?.sessionId ?? sessionId;
      time = Date.parse(value?.timestamp ?? value?.createdAt ?? '') || 0;
    } catch {
      // unreadable backup: keep key-derived session, sort as oldest
    }
    entries.push({ key, sessionId: sessionId || key, time });
  }
  return entries;
};

/** Applies the retention rules to a Storage area. Returns how many backups were removed. */
export const pruneBackups = (storage: Storage, activeSessionId?: string): number => {
  const doomed = backupKeysToDelete(readBackupEntries(storage), activeSessionId);
  doomed.forEach(key => storage.removeItem(key));
  return doomed.length;
};

export const isQuotaError = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || error.code === 22);
